import type { RecordBase } from './model';

export const manualMetricDefinitions = [
  { key: 'reach', label: 'Alcance por publicação' },
  { key: 'impressions', label: 'Impressões' },
  { key: 'video_views', label: 'Visualizações do vídeo' },
  { key: 'total_interactions', label: 'Interações totais' },
  { key: 'likes', label: 'Curtidas' },
  { key: 'comments', label: 'Comentários' },
  { key: 'shares', label: 'Compartilhamentos' },
  { key: 'saves', label: 'Salvamentos' },
  { key: 'profile_visits', label: 'Visitas ao perfil atribuídas à publicação' },
  { key: 'followers', label: 'Seguidores atribuídos à publicação' },
] as const;
export type ManualMetricKey = (typeof manualMetricDefinitions)[number]['key'];
export type ManualMetricStatus =
  'available' | 'not_reported' | 'no_permission' | 'not_applicable' | 'error';
export type ManualWindowKind = 'lifetime_snapshot' | 'native_window' | 'estimate';
export interface ManualMetricValue {
  key: ManualMetricKey;
  status: ManualMetricStatus;
  value: number | null;
  unit: 'count';
  note: string;
}
export interface ManualMetricReading extends RecordBase {
  campaignId: string;
  publicationId: string;
  publicationRecordId: string | null;
  contentId: string;
  account: string;
  platform: string;
  publicationUrl: string;
  publishedAt: string;
  publicationPrecision: 'day' | 'second';
  source: string;
  evidence: string;
  organicEvidence: string;
  scope: 'organic_declared';
  sourceVersion: string;
  collectedAt: string;
  kind: ManualWindowKind;
  windowStart: string;
  windowEnd: string;
  windowPrecision: 'day' | 'second';
  timezone: 'America/Sao_Paulo' | 'UTC';
  /** Calendar-day windows include their end day. Instant windows use an exclusive end. */
  endInclusive: boolean;
  coverage: 'complete' | 'partial';
  coverageNote: string;
  method: string;
  quality: 'observed' | 'estimated';
  ageSeconds: number | null;
  closure: {
    classification:
      | 'not_d7'
      | 'native_d7'
      | 'snapshot_at_d7'
      | 'snapshot_after_d7'
      | 'day_precision_pending'
      | 'estimate';
    dueAt: string | null;
    delaySeconds: number | null;
  };
  metrics: ManualMetricValue[];
  replacesId: string | null;
  rootReadingId: string;
  revision: number;
  correctionReason: string;
  requestId: string;
  requestHash: string;
  recordedBy: string;
}
export interface ManualMetricPublication {
  id: string;
  contentId: string;
  account: string;
  platform: string;
  url: string;
  publishedAt: string;
  precision: 'day' | 'second';
  readingIds: string[];
}
export interface ManualMetricsWorkspaceData {
  publications: ManualMetricPublication[];
  readings: ManualMetricReading[];
  currentReadings: ManualMetricReading[];
  generatedAt: string;
}
export interface ManualMetricsComparison {
  leftId: string;
  rightId: string;
  compatible: boolean;
  mode: 'same_window' | 'cumulative_evolution' | null;
  reasons: string[];
  note: string;
  metrics: {
    key: ManualMetricKey;
    leftValue: number | null;
    rightValue: number | null;
    difference: number | null;
    percentageChange: number | null;
  }[];
}
type PublicationIdentity = Pick<ManualMetricReading, 'publicationId' | 'publicationRecordId'>;
export const manualMetricPublicationRecordId = (value: PublicationIdentity) =>
  value.publicationRecordId ||
  (value.publicationId.startsWith('publication:')
    ? value.publicationId.slice('publication:'.length)
    : null);

/** Linking to Meta changes a report key, not the editorial publication's identity. */
export function sameManualMetricPublication(left: PublicationIdentity, right: PublicationIdentity) {
  const leftRecord = manualMetricPublicationRecordId(left),
    rightRecord = manualMetricPublicationRecordId(right);
  // Never merge separate destinations by piece, title or account label.
  return leftRecord && rightRecord
    ? leftRecord === rightRecord
    : left.publicationId === right.publicationId;
}
/** Corrections replace a logical observation in current views without deleting the original. */
export function currentManualMetricReadings(readings: ManualMetricReading[]) {
  const replaced = new Set(readings.map((reading) => reading.replacesId).filter(Boolean));
  return readings.filter((reading) => !replaced.has(reading.id));
}
